import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Grid,
  useMediaQuery,
  Alert,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import SubTitle from "../common/SubTitle";
import MapSection from "./MapSection";
import BoundarySection from "./BoundarySection";
import VehiclesSection from "./VehiclesSection";
import SiteDetails from "./SiteDetails";
import axios from "axios";
import config from "../../config.js";
import { Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";

async function getSiteById(id) {

  try {
    const response = await axios.get(`${config.baseURL}/site/id?id=${id}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.log("Error in fetching site data: ", error);
    throw error;
  }
}

async function createSite(site) {
  try {
    const response = await axios.post(`${config.baseURL}/site`, site, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.log("Error in creating site: ", error);
    throw error;
  }
}

async function updateSite(site) {
  try {
    const response = await axios.put(`${config.baseURL}/site`, site, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.log("Error in updating site: ", error);
    throw error;
  }
}

async function assignVehiclesToSite(id, vehicleIds) {
  try {
    const response = await axios.put(
      `${config.baseURL}/site/vehicles`,
      { siteId: id, vehicleIds },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.log("Error in assigning vehicles to site: ", error);
    throw error;
  }
}

async function getAllTheVehicles() {
  try {
    const response = await axios.get(`${config.baseURL}/vehicle/organization`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.log("Error in fetching vehicles: ", error);
    throw error;
  }
}

async function getUsersForTheOrg(organizationId) {
  try {

    const response = await axios.get(
      `${config.baseURL}/user/organization/${organizationId}`,
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.log("Error in fetching user data: ", error);
    throw error;
  }
}

async function getAllLabels() {
  const response = await axios.get(`${config.baseURL}/label`, {
    withCredentials: true,
  });
  return Array.isArray(response.data)
    ? response.data
    : response.data?.labels || response.data?.data || [];
}

async function getOrganizationGates(organizationId) {
  const response = await axios.get(`${config.baseURL}/stop/gates`, {
    withCredentials: true,
    params: { organizationId },
  });
  return Array.isArray(response.data)
    ? response.data
    : response.data?.gates || response.data?.data || [];
}

async function getSiteGates(siteId) {
  const response = await axios.get(`${config.baseURL}/stop/gates/site/${siteId}`, {
    withCredentials: true,
  });
  return Array.isArray(response.data)
    ? response.data
    : response.data?.gates || response.data?.data || [];
}

async function getStopById(id) {
  const response = await axios.get(`${config.baseURL}/stop/id`, {
    withCredentials: true,
    params: { id },
  });
  return response.data;
}

async function createStop(stopData) {
  const response = await axios.post(`${config.baseURL}/stop`, stopData, {
    withCredentials: true,
  });
  return response.data;
}

async function updateStop(stopData) {
  const response = await axios.put(`${config.baseURL}/stop`, stopData, {
    withCredentials: true,
  });
  return response.data;
}

async function assignLabel(labelId, entityId) {
  const response = await axios.post(
    `${config.baseURL}/label/${labelId}/assign`,
    { entityType: "stops", entityId },
    { withCredentials: true }
  );
  return response.data;
}

export default function AddSiteMain() {
  const { t } = useTranslation();
  const { siteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const userStateString = localStorage.getItem("userState");
  const mapRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:600px)");
  const userState = JSON.parse(userStateString);
  const organizationId = userState?.user?.organizationId || "";
  const isSchoolApp =
    localStorage.getItem("selectedApp") === "schoolTrack" ||
    location.pathname.startsWith("/school-sites");
  const sitesListPath = isSchoolApp ? "/school-sites" : "/site";

  const [site, setSite] = useState({
    siteId: "",
    name: "",
    description: "",
    siteIncharge: "",
    organizationId: organizationId,
    boundaryType: "CIRCULAR",
    sitCoordinates: [{ longitude: 0, latitude: 0 }],
    radius: "",
  });

  const [errors, setErrors] = useState({
    siteId: "",
    name: "",
    radius: "",
    siteIncharge: "",
    coordinates: [],
    general: "",
  });

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [boundaryType, setBoundaryType] = useState("circular");
  const [mapCenter, setMapCenter] = useState({ lat: 13.025, lng: 77.628 });
  const [mapLoaded, setMapLoaded] = useState(false);
  const [touched, setTouched] = useState({});
  const [mapZoom, setMapZoom] = useState(15);
  const [labels, setLabels] = useState([]);
  const [organizationGates, setOrganizationGates] = useState([]);
  const [siteGates, setSiteGates] = useState([]);
  const [selectedExistingGateId, setSelectedExistingGateId] = useState("");
  const [pendingAttachGateIds, setPendingAttachGateIds] = useState([]);
  const [pendingNewGates, setPendingNewGates] = useState([]);
  const [newGate, setNewGate] = useState({ stopId: "", name: "", longitude: "", latitude: "" });
  const [isPickingGatePoint, setIsPickingGatePoint] = useState(false);


  const [initialSiteName, setInitialSiteName] = useState("Site");

  // Gates selected via "Attach Selected Gate" should appear on the map immediately
  // (before the user presses "Save"). These are not yet part of `siteGates`.
  const savedSiteGateIds = new Set((siteGates || []).map((g) => Number(g.id)));
  const pendingAttachedGates = pendingAttachGateIds
    .map((gateId) => organizationGates.find((g) => Number(g.id) === Number(gateId)))
    // Avoid duplicates once the site save refreshes `siteGates`.
    .filter((g) => g && !savedSiteGateIds.has(Number(g.id)))
    .filter(Boolean)
    .map((g) => ({
      id: g.id,
      name: g.name,
      stopId: g.stopId,
      latitude: Number(g.latitude),
      longitude: Number(g.longitude),
    }));

  const getNormalizedCoordinate = (coordinate = {}) => ({
    ...coordinate,
    longitude: coordinate?.longitude ?? "",
    latitude: coordinate?.latitude ?? "",
  });

  const getApiBoundaryType = (value) => {
    if (typeof value !== "string" || !value.trim()) {
      return "CIRCULAR";
    }

    const normalizedValue = value.trim().toUpperCase();
    return normalizedValue === "CUSTOM" ? "CUSTOM" : "CIRCULAR";
  };

  const getFallbackCoordinates = (coordinates = []) => {
    if (Array.isArray(coordinates) && coordinates.length > 0) {
      return coordinates.map((coordinate) => getNormalizedCoordinate(coordinate));
    }

    return [getNormalizedCoordinate()];
  };

  const resolveBoundaryType = (siteData) => {
    const backendBoundaryType =
      siteData?.boundaryType || siteData?.siteType || null;
    const normalizedBoundaryType =
      typeof backendBoundaryType === "string"
        ? backendBoundaryType.toLowerCase()
        : null;

    if (
      normalizedBoundaryType === "circular" ||
      normalizedBoundaryType === "custom"
    ) {
      return normalizedBoundaryType;
    }

    return "circular";
  };

  const normalizeSiteForBoundaryType = (siteData, nextBoundaryType) => {
    const normalizedCoordinates = getFallbackCoordinates(siteData?.sitCoordinates);
    const apiBoundaryType = getApiBoundaryType(nextBoundaryType);

    if (nextBoundaryType === "circular") {
      return {
        ...siteData,
        boundaryType: apiBoundaryType,
        radius: siteData?.radius ?? "",
        sitCoordinates: [normalizedCoordinates[0]],
      };
    }

    return {
      ...siteData,
      boundaryType: apiBoundaryType,
      radius: "",
      sitCoordinates: normalizedCoordinates,
    };
  };

  const buildSitePayload = (currentSite, currentBoundaryType, isEditing) => {
    const normalizedSite = normalizeSiteForBoundaryType(
      currentSite,
      currentBoundaryType
    );
    const boundaryType = getApiBoundaryType(currentBoundaryType);
    const cleanedCoordinates = normalizedSite.sitCoordinates
      .filter((coord) => coord.longitude !== "" && coord.latitude !== "")
      .map((coord) => ({
        latitude: Number(coord.latitude),
        longitude: Number(coord.longitude),
      }))
      .filter(
        (coord) =>
          !Number.isNaN(coord.latitude) && !Number.isNaN(coord.longitude)
      );

    const payload = {
      siteId: normalizedSite.siteId,
      name: normalizedSite.name,
      description: normalizedSite.description || "",
      organizationId: Number(normalizedSite.organizationId),
      siteIncharge: normalizedSite.siteIncharge || null,
      boundaryType,
      radius:
        boundaryType === "CIRCULAR" && normalizedSite.radius !== ""
          ? parseFloat(normalizedSite.radius)
          : null,
      sitCoordinates:
        boundaryType === "CIRCULAR"
          ? cleanedCoordinates.slice(0, 1)
          : cleanedCoordinates,
    };

    if (isEditing && currentSite?.id) {
      payload.id = currentSite.id;
    }

    return payload;
  };

  const handleBoundaryTypeChange = (nextBoundaryType) => {
    setBoundaryType(nextBoundaryType);
    setSite((prevSite) => normalizeSiteForBoundaryType(prevSite, nextBoundaryType));
    setErrors((prev) => ({
      ...prev,
      radius: "",
      general: "",
      coordinates: [],
    }));
  };

  const calculateZoomLevel = (radiusInKm) => {
    if (!radiusInKm) return 15;
    const radius = parseFloat(radiusInKm);
    if (radius <= 0.1) return 18;
    if (radius <= 0.25) return 17;
    if (radius <= 0.5) return 16;
    if (radius <= 1) return 15;
    if (radius <= 2) return 14;
    if (radius <= 5) return 13;
    if (radius <= 10) return 12;
    if (radius <= 20) return 11;
    if (radius <= 40) return 10;
    if (radius <= 80) return 9;
    if (radius <= 100) return 8;
    return 7;
  };

  const calculatePolygonZoomAndCenter = (coordinates) => {
    if (!coordinates || coordinates.length === 0) {
      return { center: { lat: 13.025, lng: 77.628 }, zoom: 14 };
    }

    const lats = coordinates
      .map((coord) => parseFloat(coord.latitude))
      .filter((lat) => !isNaN(lat));
    const lngs = coordinates
      .map((coord) => parseFloat(coord.longitude))
      .filter((lng) => !isNaN(lng));

    if (lats.length === 0 || lngs.length === 0) {
      return { center: { lat: 13.025, lng: 77.628 }, zoom: 10 };
    }

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const center = {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    };

    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    let zoom;
    if (maxDiff < 0.01) zoom = 15;
    else if (maxDiff < 0.05) zoom = 13;
    else if (maxDiff < 0.1) zoom = 12;
    else if (maxDiff < 0.5) zoom = 10;
    else if (maxDiff < 1) zoom = 9;
    else if (maxDiff < 5) zoom = 8;
    else zoom = 7;

    return { center, zoom };
  };

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        let currentSiteId = null;
        if (siteId) {
          setIsUpdating(true);
          const siteData = await getSiteById(siteId);
          currentSiteId = siteData?.id || null;
          const resolvedBoundaryType = resolveBoundaryType(siteData);
          const normalizedSiteData = normalizeSiteForBoundaryType(
            {
            ...siteData,
            siteIncharge: siteData.siteIncharge || "",
            },
            resolvedBoundaryType
          );

          setSite(normalizedSiteData);
          setInitialSiteName(siteData.name || "Site");

          if (
            normalizedSiteData.sitCoordinates &&
            normalizedSiteData.sitCoordinates.length > 0
          ) {
            if (resolvedBoundaryType === "circular" && normalizedSiteData.radius) {
              setMapCenter({
                lat: parseFloat(normalizedSiteData.sitCoordinates[0].latitude) || 0,
                lng: parseFloat(normalizedSiteData.sitCoordinates[0].longitude) || 0,
              });
              setMapZoom(calculateZoomLevel(normalizedSiteData.radius));
            } else {
              const { center, zoom } = calculatePolygonZoomAndCenter(
                normalizedSiteData.sitCoordinates
              );
              setMapCenter(center);
              setMapZoom(zoom);
            }
          }
          setBoundaryType(resolvedBoundaryType);
          if (siteData.vehicles && siteData.vehicles.length > 0) {
            setSelectedVehicles(siteData.vehicles.map((v) => v.id));
          }
          const existingSiteGates = await getSiteGates(siteData.id);
          setSiteGates(existingSiteGates);
        }
        const vehicleData = await getAllTheVehicles();
        const vehicleList = Array.isArray(vehicleData) ? vehicleData : [];
        const availableVehicles = vehicleList.filter((vehicle) => {
          const assignedSiteId = vehicle?.siteId ?? vehicle?.site?.id ?? null;
          if (assignedSiteId == null || assignedSiteId === "") return true;
          return (
            currentSiteId != null &&
            String(assignedSiteId) === String(currentSiteId)
          );
        });

        setVehicles(availableVehicles);
        const [labelData, orgGates] = await Promise.all([
          getAllLabels(),
          organizationId ? getOrganizationGates(organizationId) : Promise.resolve([]),
        ]);
        setLabels(labelData);
        setOrganizationGates(orgGates);

        if (organizationId) {
          const userData = await getUsersForTheOrg(organizationId);
          console.log("userid anveed checck", userData);
          setUsers(userData.users);
        }
      } catch (err) {
        setNotification({
          open: true,
          message: `Failed to load data: ${
            err.response?.data?.message || err.message
          }`,
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [siteId, organizationId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "radius" && value) {
      const numValue = parseFloat(value);
      if (numValue > 100) {
        newValue = "100";
      }
    }

    setSite((prevSite) => ({
      ...prevSite,
      [name]: newValue,
    }));

    if (name === "radius" && newValue) {
      setMapZoom(calculateZoomLevel(newValue));
    }

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCoordinatesChange = (index, key, value) => {
    const newCoordinates = [...site.sitCoordinates];
    if (index >= newCoordinates.length) {
      // Add a new coordinate if the index doesn't exist
      newCoordinates.push({ longitude: "", latitude: "" });
    }
    newCoordinates[index][key] = value;

    const newTouched = { ...touched };
    newTouched[`coordinate_${index}_${key}`] = true;
    setTouched(newTouched);

    if (errors.coordinates && errors.coordinates[index]) {
      const newCoordErrors = [...errors.coordinates];
      newCoordErrors[index] = "";
      setErrors((prev) => ({
        ...prev,
        coordinates: newCoordErrors,
      }));
    }

    if (index === 0 && boundaryType === "circular") {
      setMapCenter({
        lat:
          key === "latitude"
            ? parseFloat(value) || mapCenter.lat
            : parseFloat(newCoordinates[0].latitude) || mapCenter.lat,
        lng:
          key === "longitude"
            ? parseFloat(value) || mapCenter.lng
            : parseFloat(newCoordinates[0].longitude) || mapCenter.lng,
      });
    } else if (boundaryType === "custom") {
      // Update map center based on the average of all coordinates
      const validCoords = newCoordinates.filter(
        (coord) => coord.latitude && coord.longitude
      );
      if (validCoords.length > 0) {
        const { center } = calculatePolygonZoomAndCenter(validCoords);
        setMapCenter(center);
      }
    }

    setSite((prevSite) => ({
      ...prevSite,
      sitCoordinates: newCoordinates,
    }));
  };

  const handleMapClick = (e) => {
    if (isPickingGatePoint) {
      setNewGate((prev) => ({
        ...prev,
        longitude: e.latLng.lng().toFixed(6),
        latitude: e.latLng.lat().toFixed(6),
      }));
      setIsPickingGatePoint(false);
      return;
    }

    if (boundaryType === "circular") {
      const newCoordinates = [
        {
          longitude: e.latLng.lng().toFixed(6),
          latitude: e.latLng.lat().toFixed(6),
        },
      ];
      setSite((prev) => ({
        ...prev,
        sitCoordinates: newCoordinates,
      }));
      setMapCenter({
        lat: parseFloat(e.latLng.lat()),
        lng: parseFloat(e.latLng.lng()),
      });
    } else {
      // For custom boundary, check if last coordinate is empty
      const lastCoord = site.sitCoordinates[site.sitCoordinates.length - 1];
      if (!lastCoord || (lastCoord.longitude && lastCoord.latitude)) {
        const newCoordinate = {
          longitude: e.latLng.lng().toFixed(6),
          latitude: e.latLng.lat().toFixed(6),
        };
        setSite((prev) => ({
          ...prev,
          sitCoordinates: [...prev.sitCoordinates, newCoordinate],
        }));
        setErrors((prev) => ({
          ...prev,
          coordinates: [...(prev.coordinates || []), ""],
        }));
      } else {
        // Update the last empty coordinate
        handleCoordinatesChange(
          site.sitCoordinates.length - 1,
          "longitude",
          e.latLng.lng().toFixed(6)
        );
        handleCoordinatesChange(
          site.sitCoordinates.length - 1,
          "latitude",
          e.latLng.lat().toFixed(6)
        );
      }
    }
  };

  const handleMarkerDrag = (index, e) => {
    const newCoordinates = [...site.sitCoordinates];
    newCoordinates[index] = {
      longitude: e.latLng.lng().toFixed(6),
      latitude: e.latLng.lat().toFixed(6),
    };

    if (index === 0 && boundaryType === "circular") {
      setMapCenter({
        lat: parseFloat(e.latLng.lat()),
        lng: parseFloat(e.latLng.lng()),
      });
    }

    setSite((prev) => ({
      ...prev,
      sitCoordinates: newCoordinates,
    }));
  };

  const handlePlaceSelected = (place) => {
    if (!place.geometry || !place.geometry.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    if (boundaryType === "circular") {
      const newCoordinates = [
        {
          longitude: lng.toFixed(6),
          latitude: lat.toFixed(6),
        },
      ];
      setSite((prev) => ({
        ...prev,
        sitCoordinates: newCoordinates,
      }));
    } else {
      const newCoordinate = {
        longitude: lng.toFixed(6),
        latitude: lat.toFixed(6),
      };
      setSite((prev) => ({
        ...prev,
        sitCoordinates: [...prev.sitCoordinates, newCoordinate],
      }));
      setErrors((prev) => ({
        ...prev,
        coordinates: [...(prev.coordinates || []), ""],
      }));
    }

    setMapCenter({ lat, lng });
    setMapZoom(18);
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
    validateField(field);
  };

  const handleCoordinateBlur = (index, key) => {
    const fieldName = `coordinate_${index}_${key}`;
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
    validateCoordinate(index, key);
  };

  const validateField = (field) => {
    let newErrors = { ...errors };

    switch (field) {
      case "siteId":
        if (!site.siteId.trim()) {
          newErrors.siteId = "Site ID is required";
        } else if (!/^[a-zA-Z0-9_-]+$/.test(site.siteId)) {
          newErrors.siteId =
            "Site ID should only contain letters, numbers, hyphens, and underscores";
        } else {
          newErrors.siteId = "";
        }
        break;

      case "name":
        if (!site.name.trim()) {
          newErrors.name = "Site name is required";
        } else {
          newErrors.name = "";
        }
        break;

      case "radius":
        if (boundaryType === "circular") {
          if (!site.radius) {
            newErrors.radius = "Radius is required for circular boundary";
          } else if (parseFloat(site.radius) <= 0) {
            newErrors.radius = "Radius must be greater than 0";
          } else if (parseFloat(site.radius) > 100) {
            newErrors.radius = "Radius cannot exceed 100";
          } else {
            newErrors.radius = "";
          }
        } else {
          newErrors.radius = "";
        }
        break;

      case "siteIncharge":
        // Optional field, no validation needed unless specific rules apply
        newErrors.siteIncharge = "";
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return !newErrors[field];
  };

  const validateCoordinate = (index, key) => {
    const coordinate = site.sitCoordinates[index];
    const value = coordinate[key];
    let newCoordErrors = [
      ...(errors.coordinates || Array(site.sitCoordinates.length).fill("")),
    ];

    if (key === "longitude") {
      if (value === "" || value === null) {
        newCoordErrors[index] = "Longitude is required";
      } else if (
        isNaN(value) ||
        parseFloat(value) < -180 ||
        parseFloat(value) > 180
      ) {
        newCoordErrors[index] = "Longitude must be between -180 and 180";
      } else {
        newCoordErrors[index] = "";
      }
    }

    if (key === "latitude") {
      if (value === "" || value === null) {
        newCoordErrors[index] = "Latitude is required";
      } else if (
        isNaN(value) ||
        parseFloat(value) < -90 ||
        parseFloat(value) > 90
      ) {
        newCoordErrors[index] = "Latitude must be between -90 and 90";
      } else {
        newCoordErrors[index] = "";
      }
    }

    setErrors((prev) => ({
      ...prev,
      coordinates: newCoordErrors,
    }));

    return !newCoordErrors[index];
  };

  const validatePolygon = () => {
    if (boundaryType !== "custom" || site.sitCoordinates.length < 3) {
      return true;
    }

    const uniquePoints = new Set();
    let isValid = true;

    site.sitCoordinates.forEach((coord, index) => {
      const pointKey = `${coord.longitude},${coord.latitude}`;
      if (uniquePoints.has(pointKey)) {
        const newCoordErrors = [
          ...(errors.coordinates || Array(site.sitCoordinates.length).fill("")),
        ];
        newCoordErrors[index] = "Duplicate coordinate point";
        setErrors((prev) => ({
          ...prev,
          coordinates: newCoordErrors,
        }));
        isValid = false;
      }
      uniquePoints.add(pointKey);
    });

    return isValid;
  };

  const validateForm = () => {
    let isFormValid = true;
    let newErrors = { ...errors };

    if (!site.siteId.trim()) {
      newErrors.siteId = "Site ID is required";
      isFormValid = false;
    } else if (!/^[a-zA-Z0-9_-]+$/.test(site.siteId)) {
      newErrors.siteId =
        "Site ID should only contain letters, numbers, hyphens, and underscores";
      isFormValid = false;
    } else {
      newErrors.siteId = "";
    }

    if (!site.name.trim()) {
      newErrors.name = "Site name is required";
      isFormValid = false;
    } else {
      newErrors.name = "";
    }

    newErrors.siteIncharge = ""; // Optional field

    const newCoordErrors = Array(site.sitCoordinates.length).fill("");

    site.sitCoordinates.forEach((coord, index) => {
      if (coord.longitude === "" || coord.longitude === null) {
        newCoordErrors[index] = "Longitude is required";
        isFormValid = false;
      } else if (
        isNaN(coord.longitude) ||
        parseFloat(coord.longitude) < -180 ||
        parseFloat(coord.longitude) > 180
      ) {
        newCoordErrors[index] = "Longitude must be between -180 and 180";
        isFormValid = false;
      }

      if (coord.latitude === "" || coord.latitude === null) {
        newCoordErrors[index] = newCoordErrors[index]
          ? `${newCoordErrors[index]} and Latitude is required`
          : "Latitude is required";
        isFormValid = false;
      } else if (
        isNaN(coord.latitude) ||
        parseFloat(coord.latitude) < -90 ||
        parseFloat(coord.latitude) > 90
      ) {
        newCoordErrors[index] = newCoordErrors[index]
          ? `${newCoordErrors[index]} and Latitude must be between -90 and 90`
          : "Latitude must be between -90 and 90";
        isFormValid = false;
      }
    });

    newErrors.coordinates = newCoordErrors;

    if (boundaryType === "circular") {
      if (!site.radius) {
        newErrors.radius = "Radius is required for circular boundary";
        isFormValid = false;
      } else if (parseFloat(site.radius) <= 0) {
        newErrors.radius = "Radius must be greater than 0";
        isFormValid = false;
      } else if (parseFloat(site.radius) > 100) {
        newErrors.radius = "Radius cannot exceed 100";
        isFormValid = false;
      } else {
        newErrors.radius = "";
      }
    } else {
      newErrors.radius = "";
    }

    if (boundaryType === "custom" && site.sitCoordinates.length < 3) {
      newErrors.general = "Custom boundary requires at least 3 coordinates";
      isFormValid = false;
    } else if (boundaryType === "custom") {
      isFormValid = validatePolygon() && isFormValid;
    }

    setErrors(newErrors);
    return isFormValid;
  };

  const addCoordinate = () => {
    // Only add new coordinate if last coordinate has values
    const lastCoord = site.sitCoordinates[site.sitCoordinates.length - 1];
    if (
      site.sitCoordinates.length === 0 ||
      (lastCoord && lastCoord.longitude && lastCoord.latitude)
    ) {
      setSite((prevSite) => ({
        ...prevSite,
        sitCoordinates: [
          ...prevSite.sitCoordinates,
          { longitude: "", latitude: "" },
        ],
      }));
      setErrors((prev) => ({
        ...prev,
        coordinates: [...(prev.coordinates || []), ""],
      }));
    }
  };

  const removeCoordinate = (index) => {
    if (site.sitCoordinates.length > 1) {
      const newCoordinates = [...site.sitCoordinates];
      newCoordinates.splice(index, 1);
      setSite((prevSite) => ({
        ...prevSite,
        sitCoordinates: newCoordinates,
      }));
      const newCoordErrors = [...(errors.coordinates || [])];
      newCoordErrors.splice(index, 1);
      setErrors((prev) => ({
        ...prev,
        coordinates: newCoordErrors,
      }));
    }
  };

  const handleVehicleSelection = (event) => {
    setSelectedVehicles(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const allTouched = {
      siteId: true,
      name: true,
      siteIncharge: true,
      radius: boundaryType === "circular",
    };

    site.sitCoordinates.forEach((_, index) => {
      allTouched[`coordinate_${index}_longitude`] = true;
      allTouched[`coordinate_${index}_latitude`] = true;
    });

    setTouched(allTouched);

    if (!validateForm()) {
      setNotification({
        open: true,
        message: "Please correct the errors before submitting",
        severity: "error",
      });
      return;
    }

    setIsLoading(true);
    const siteData = buildSitePayload(site, boundaryType, isUpdating);

    const resolveGateLabelId = () => {
      const gateByType = labels.find((l) => String(l.type || "").toUpperCase() === "GATE");
      if (gateByType) return gateByType.id;
      const gateByName = labels.find((l) => String(l.name || "").toLowerCase() === "gate");
      return gateByName?.id || null;
    };

    const processPendingGates = async (resolvedSiteId) => {
      if (!resolvedSiteId) return;
      const gateLabelId = resolveGateLabelId();
      if (!gateLabelId && (pendingNewGates.length > 0 || pendingAttachGateIds.length > 0)) {
        throw new Error("Gate label not found. Please create a label with type GATE.");
      }

      // Create brand-new gates from selected map points.
      for (const gate of pendingNewGates) {
        const created = await createStop({
          stopId: gate.stopId,
          name: gate.name,
          longitude: Number(gate.longitude),
          latitude: Number(gate.latitude),
          organizationId: Number(organizationId),
          siteId: Number(resolvedSiteId),
          type: "STOP",
        });
        const createdStopId = created?.id || created?.stop?.id || created?.stopId;
        if (createdStopId) {
          await assignLabel(gateLabelId, Number(createdStopId));
        }
      }

      // Attach selected existing gates to this site and ensure they are labeled GATE.
      for (const gateId of pendingAttachGateIds) {
        const stopData = await getStopById(Number(gateId));
        await updateStop({
          id: Number(gateId),
          stopId: stopData.stopId,
          name: stopData.name,
          organizationId: Number(stopData.organizationId || organizationId),
          longitude: stopData.longitude,
          latitude: stopData.latitude,
          landmark: stopData.landmark || "",
          description: stopData.description || "",
          type: stopData.type || "STOP",
          siteId: Number(resolvedSiteId),
        });
        await assignLabel(gateLabelId, Number(gateId));
      }
    };

    const action = isUpdating ? updateSite : createSite;
    action(siteData)
      .then((savedSite) => {
        const resolvedSiteId =
          savedSite?.id || site?.id || (siteId ? Number(siteId) : null);

        // Always sync on update, including an empty list, so unassigned vehicles are cleared.
        const shouldSyncVehicles =
          Boolean(resolvedSiteId) && (isUpdating || selectedVehicles.length > 0);
        const runVehicleAssign = shouldSyncVehicles
          ? assignVehiclesToSite(resolvedSiteId, selectedVehicles)
          : Promise.resolve();

        return runVehicleAssign
          .then(() => processPendingGates(resolvedSiteId))
          .then(async () => {
            if (resolvedSiteId) {
              const [freshOrgGates, freshSiteGates] = await Promise.all([
                getOrganizationGates(organizationId),
                getSiteGates(resolvedSiteId),
              ]);
              setOrganizationGates(freshOrgGates);
              setSiteGates(freshSiteGates);
            }
            return savedSite;
          });
      })
      .then(() => {
        setNotification({
          open: true,
          message: `Site ${isUpdating ? "updated" : "created"} successfully`,
          severity: "success",
        });
        navigate(sitesListPath, { replace: true });
      })
      .catch((err) => {
        const errorMessage = err.response?.data?.message || err.message;
        if (
          err.response?.data?.code === "P2002" &&
          errorMessage.includes("siteIncharge")
        ) {
          setErrors((prev) => ({
            ...prev,
            siteIncharge: "Site Incharge is already assigned to another site",
          }));
          setNotification({
            open: true,
            message: "Site Incharge is already assigned to another site",
            severity: "error",
          });
        } else {
          setNotification({
            open: true,
            message: `Failed to ${
              isUpdating ? "update" : "create"
            } site: ${errorMessage}`,
            severity: "error",
          });
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleCancel = () => {
    navigate(sitesListPath);
  };

  const createVehicle = () => {
    navigate("/vehicle/add");
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
    setMapLoaded(true);
  };

  const queueCreateGate = () => {
    if (!newGate.stopId || !newGate.name || !newGate.longitude || !newGate.latitude) {
      setNotification({
        open: true,
        message: t(
          "Please fill Gate Stop ID, Gate Name, and longitude/latitude (enter manually or pick on the map).",
        ),
        severity: "error",
      });
      return;
    }
    setPendingNewGates((prev) => [...prev, newGate]);
    setNewGate({ stopId: "", name: "", longitude: "", latitude: "" });
  };

  const removePendingNewGate = (indexToRemove) => {
    setPendingNewGates((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const queueAttachExistingGate = () => {
    if (!selectedExistingGateId) return;
    setPendingAttachGateIds((prev) =>
      prev.includes(Number(selectedExistingGateId))
        ? prev
        : [...prev, Number(selectedExistingGateId)]
    );
    setSelectedExistingGateId("");
  };

  const removePendingAttachedGate = (gateId) => {
    setPendingAttachGateIds((prev) => prev.filter((id) => id !== gateId));
  };

  const detachGateFromSite = async (gateId) => {
    if (!gateId) return;

    const shouldDetach = window.confirm(
      t(
        "Detach this gate from the site? The gate stop and GATE label will remain available for organization-level use.",
      ),
    );
    if (!shouldDetach) return;

    try {
      setIsLoading(true);
      const stopData = await getStopById(Number(gateId));
      await updateStop({
        id: Number(gateId),
        stopId: stopData.stopId,
        name: stopData.name,
        organizationId: Number(stopData.organizationId || organizationId),
        longitude: stopData.longitude,
        latitude: stopData.latitude,
        landmark: stopData.landmark || "",
        description: stopData.description || "",
        type: stopData.type || "STOP",
        siteId: null,
      });

      const currentSiteId = site?.id || (siteId ? Number(siteId) : null);
      const [freshOrgGates, freshSiteGates] = await Promise.all([
        organizationId ? getOrganizationGates(organizationId) : Promise.resolve([]),
        currentSiteId ? getSiteGates(currentSiteId) : Promise.resolve([]),
      ]);
      setOrganizationGates(freshOrgGates);
      setSiteGates(freshSiteGates);
      setNotification({
        open: true,
        message: "Gate detached from site successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: `Failed to detach gate: ${err.response?.data?.message || err.message}`,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  return (
    <>
      <SubTitle
        title={isUpdating ? "Update Site" : "Create Site"}
        path={
          isUpdating
            ? `${orgName || "organization"} > Sites > ${initialSiteName} `
            : `${orgName || "organization"} > Sites > Create`
        }
      />
      {/* <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar> */}

      {isLoading ? (
        <Box
          sx={{ margin: { xs: "0px 16px", sm: "0px 24px", md: "0px 40px" } }}
        >
          <Box
            sx={{
              minHeight: { xs: "auto", md: "556px" },
              borderRadius: "10px",
              padding: { xs: "16px", sm: "20px", md: "24px" },
              background: "#FFFFFF",
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 1px 5px 0px #00000040",
              mb: 3,
              overflow: "hidden",
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rectangular" height={350} sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={350} sx={{ mb: 2 }} />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              borderRadius: "10px",
              padding: { xs: "16px", sm: "20px", md: "24px 24px 32px 24px" },
              background: "#FFFFFF",
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 2px 5px 0px #00000040",
              overflow: "hidden",
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
              </Grid>
            </Grid>
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}
            >
              <Skeleton variant="rectangular" width={122} height={55} />
              <Skeleton variant="rectangular" width={125} height={55} />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            margin: { xs: "0px 16px", sm: "0px 24px", md: "0px 40px" },
          }}
        >
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.general}
            </Alert>
          )}

          <Box
            sx={{
              minHeight: { xs: "auto", md: "556px" },
              borderRadius: "10px",
              padding: { xs: "16px", sm: "20px", md: "24px" },
              gap: "20px",
              background: "#FFFFFF",
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 1px 5px 0px #00000040",
              mb: 3,
              overflow: "hidden",
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} md={8}>
                <MapSection
                  boundaryType={boundaryType}
                  site={site}
                  mapCenter={mapCenter}
                  mapZoom={mapZoom}
                  mapLoaded={mapLoaded}
                  onMapLoad={onMapLoad}
                  onMapClick={handleMapClick}
                  onMarkerDrag={handleMarkerDrag}
                  onPlaceSelected={handlePlaceSelected}
                  isMobile={isMobile}
                  siteGates={siteGates}
                    pendingAttachedGates={pendingAttachedGates}
                  pendingGateCoordinates={
                    newGate.longitude && newGate.latitude
                      ? { lat: Number(newGate.latitude), lng: Number(newGate.longitude) }
                      : null
                  }
                  pendingNewGates={pendingNewGates.map((g, idx) => ({
                    id: idx,
                    latitude: Number(g.latitude),
                    longitude: Number(g.longitude),
                    name: g.name,
                    stopId: g.stopId,
                  }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <BoundarySection
                  boundaryType={boundaryType}
                  handleBoundaryTypeChange={handleBoundaryTypeChange}
                  site={site}
                  handleCoordinatesChange={handleCoordinatesChange}
                  handleInputChange={handleInputChange}
                  addCoordinate={addCoordinate}
                  removeCoordinate={removeCoordinate}
                  errors={errors}
                  touched={touched}
                  handleCoordinateBlur={handleCoordinateBlur}
                  handleBlur={handleBlur}
                  isMobile={isMobile}
                  setErrors={setErrors}
                  setTouched={setTouched}
                />
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              borderRadius: "10px",
              padding: { xs: "16px", sm: "20px", md: "24px 24px 32px 24px" },
              gap: { xs: "20px", md: "36px" },
              background: "#FFFFFF",
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 2px 5px 0px #00000040",
              overflow: "hidden",
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} md={8}>
                <SiteDetails
                  site={site}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  isUpdating={isUpdating}
                  users={users}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 2, p: 2, border: "1px solid #A5DCF3", borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: "#183059", mb: 1 }}>
                    {t("Create a new gate on this site or attach an existing gate.")}
                  </Typography>

                  <TextField
                    size="small"
                    fullWidth
                    label={t("Gate Stop ID")}
                    value={newGate.stopId}
                    onChange={(e) => setNewGate((p) => ({ ...p, stopId: e.target.value }))}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label={t("Gate Name")}
                    value={newGate.name}
                    onChange={(e) => setNewGate((p) => ({ ...p, name: e.target.value }))}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label={t("Longitude")}
                      value={newGate.longitude}
                      onChange={(e) =>
                        setNewGate((p) => ({ ...p, longitude: e.target.value }))
                      }
                      inputProps={{ inputMode: "decimal" }}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label={t("Latitude")}
                      value={newGate.latitude}
                      onChange={(e) =>
                        setNewGate((p) => ({ ...p, latitude: e.target.value }))
                      }
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Button
                      size="small"
                      variant={isPickingGatePoint ? "contained" : "outlined"}
                      onClick={() => setIsPickingGatePoint((v) => !v)}
                    >
                      {isPickingGatePoint ? t("Click map now...") : t("Pick Gate Point")}
                    </Button>
                    <Button size="small" variant="contained" onClick={queueCreateGate}>
                      {t("Add Gate")}
                    </Button>
                  </Box>

                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <Select
                      displayEmpty
                      value={selectedExistingGateId}
                      onChange={(e) => setSelectedExistingGateId(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t("Select existing gate")}</em>
                      </MenuItem>
                      {organizationGates.map((g) => (
                        <MenuItem key={g.id} value={String(g.id)}>
                          {g.name || g.stopId || `${t("Gate")} ${g.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" variant="outlined" onClick={queueAttachExistingGate}>
                    {t("Assign Selected Gate")}
                  </Button>

                  {siteGates.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#183059", mb: 1 }}>
                        {t("Gates attached to this site")}
                      </Typography>
                      {siteGates.map((gate) => (
                        <Box
                          key={gate.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 0.5,
                            borderBottom: "1px solid #E8EAEE",
                          }}
                        >
                          <Typography sx={{ fontSize: 12, color: "#1F242E" }}>
                            {gate.name || gate.stopId || `${t("Gate")} ${gate.id}`}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            variant="text"
                            onClick={() => detachGateFromSite(gate.id)}
                          >
                            {t("Detach")}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {pendingNewGates.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#183059", mb: 1 }}>
                        {t("New gates pending save")}
                      </Typography>
                      {pendingNewGates.map((gate, index) => (
                        <Box
                          key={`${gate.stopId}-${index}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 0.5,
                            borderBottom: "1px solid #E8EAEE",
                          }}
                        >
                          <Typography sx={{ fontSize: 12, color: "#1F242E" }}>
                            {gate.name || gate.stopId}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            variant="text"
                            onClick={() => removePendingNewGate(index)}
                          >
                            {t("Remove")}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {pendingAttachGateIds.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#183059", mb: 1 }}>
                        {t("Existing gates pending attach")}
                      </Typography>
                      {pendingAttachGateIds.map((gateId) => {
                        const gate = organizationGates.find((g) => Number(g.id) === gateId);
                        return (
                          <Box
                            key={gateId}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              py: 0.5,
                              borderBottom: "1px solid #E8EAEE",
                            }}
                          >
                            <Typography sx={{ fontSize: 12, color: "#1F242E" }}>
                              {gate?.name || gate?.stopId || `${t("Gate")} ${gateId}`}
                            </Typography>
                            <Button
                              size="small"
                              color="error"
                              variant="text"
                              onClick={() => removePendingAttachedGate(gateId)}
                            >
                              {t("Remove")}
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {(pendingNewGates.length > 0 || pendingAttachGateIds.length > 0) && (
                    <Typography sx={{ mt: 1, fontSize: 12, color: "#0b5" }}>
                      {t(
                        "Pending gate changes: {{newCount}} new, {{existingCount}} existing. Save Site to persist.",
                        {
                          newCount: pendingNewGates.length,
                          existingCount: pendingAttachGateIds.length,
                        },
                      )}
                    </Typography>
                  )}
                </Box>

                <VehiclesSection
                  vehicles={vehicles}
                  selectedVehicles={selectedVehicles}
                  handleVehicleSelection={handleVehicleSelection}
                  createVehicle={createVehicle}
                  isMobile={isMobile}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 3,
                flexDirection: { xs: isMobile ? "column" : "row" },
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancel}
                disabled={isLoading}
                sx={{
                  width: "122px",
                  height: "55px",
                  borderRadius: "8px",
                  borderWidth: "1px",
                  padding: "16px 24px",
                  background: "#F2F2F3",
                  border: "1px solid #414958",
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "100%",
                  textTransform: "none",
                  letterSpacing: "0%",
                  color: "#111214",
                  "&:hover": { bgcolor: "#E0E0E0", borderColor: "#D0D0D0" },
                  width: { xs: isMobile ? "100%" : "122px" },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  width: "125px",
                  height: "55px",
                  borderRadius: "8px",
                  borderWidth: "1px",
                  padding: "16px 24px",
                  background: "#F9E719",
                  border: "1px solid #414958",
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "100%",
                  textTransform: "none",
                  color: "#111214",
                  "&:hover": { bgcolor: "#E0D618" },
                  width: { xs: isMobile ? "100%" : "125px" },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : isUpdating ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
